/*
 * FilterGroupForTag is a filter group component that allows users to filter content by tags.
 * It provides a multi-select interface for choosing tags, with support for:
 *
 * - Selecting multiple tags
 * - Filtering untagged items
 * - Custom untagged text based on context (pins vs tasks)
 *
 * The component integrates with the FilterSettings system and uses FilterGroupMultiSelect for its UI implementation.
 */

import { FilterSettings, TagName } from '@range.io/basic-types'
import { arrayToLookupTable, pluck, without } from '@range.io/functional'
import LookupTable from '@range.io/functional/src/lookup-table.js'
import React from 'react'
import { FlexRow } from '../../components-reusable/index.js'
import TagsDisplay from '../TagsDisplay.js'
import FilterGroupBaseGroup from './filter-primitives/FilterGroupBaseGroup.js'

const TagDetails = ({ filter, allTags, selectedTags, filterSettings, setFilterSettings, untaggedOptionText }) => {
    const { matchUntagged } = filterSettings
    const selectedTagIds = pluck('id', selectedTags)
    const allTagDatas = arrayToLookupTable('id', allTags)
    const update = func => v => setFilterSettings(func(filterSettings, v))

    const handleTagFilterClick = tagIdClicked => {
        const clickedTag = allTags.get(tagIdClicked)

        selectedTags = selectedTags.includes(clickedTag)
            ? without(clickedTag, selectedTags)
            : [...selectedTags, clickedTag]
        const lookupTable = LookupTable(
            selectedTags.sort((a, b) => a.name.localeCompare(b.name)),
            TagName
        )
        update(filter.setTags)(lookupTable)
    }

    return (
        <FlexRow css={{ gap: 8, flexWrap: 'wrap', width: 290 }}>
            <TagsDisplay
                variant="tagFilter"
                tags={allTagDatas}
                selectedTagIds={selectedTagIds}
                onItemSelectionChange={handleTagFilterClick}
                matchUntagged={matchUntagged}
                setMatchUntagged={update(filter.toggleMatchUntagged)}
                untaggedOptionText={untaggedOptionText}
            />
        </FlexRow>
    )
}

const FilterGroupForTag = ({ allTags, selectedTags, untaggedOptionText, filterSettings, setFilterSettings }) => {
    const updateFilterSettings = f => v => setFilterSettings(f(filterSettings, v))
    const { shouldFilterByTags } = filterSettings

    return (
        <FilterGroupBaseGroup
            title="Filter by Tags"
            checked={shouldFilterByTags}
            onToggle={updateFilterSettings(FilterSettings.setShouldFilterByTags)}
            width={320}
            dataCy="filter-tag-group"
            fullPadding
        >
            <TagDetails
                filter={FilterSettings}
                allTags={allTags}
                selectedTags={selectedTags}
                filterSettings={filterSettings}
                setFilterSettings={setFilterSettings}
                untaggedOptionText={untaggedOptionText}
            />
        </FilterGroupBaseGroup>
    )
}

export default FilterGroupForTag
